import React from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Building2, Plug, Palette, Mail } from "lucide-react";
import { useTranslation } from "../i18n";

import SuperUsersSection from "../components/Admin/SuperUsersSection";
import OrganizationsSection from "../components/Admin/OrganizationsSection";
import IntegrationsSection from "../components/Admin/IntegrationsSection";
import CustomizationSection from "../components/Admin/CustomizationSection";
import EmailDiagnosticsSection from "../components/Admin/EmailDiagnosticsSection";

// Labels are looked up inside the component.
const tabs = [
  { id: "users", icon: Users },
  { id: "organizations", icon: Building2 },
  { id: "integrations", icon: Plug },
  { id: "customization", icon: Palette },
  { id: "email", icon: Mail },
];

const SuperAdminPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation("admin");
  const currentTab = searchParams.get("tab") || "users";

  const tabLabels = {
    users: t("super_admin.tabs.users"),
    organizations: t("super_admin.tabs.organizations"),
    integrations: t("super_admin.tabs.integrations"),
    customization: t("super_admin.tabs.customization"),
    email: t("super_admin.tabs.email"),
  };

  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  const renderSection = () => {
    switch (currentTab) {
      case "users":
        return <SuperUsersSection />;
      case "organizations":
        return <OrganizationsSection />;
      case "integrations":
        return <IntegrationsSection />;
      case "customization":
        return <CustomizationSection />;
      case "email":
        return <EmailDiagnosticsSection />;
      default:
        return <SuperUsersSection />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-3 sm:pt-8">
        {/* Tabs Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300 p-2">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = currentTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-medium transition-all duration-200 ${
                      isActive
                        ? "org-gradient text-white shadow-md"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="hidden sm:inline">{tabLabels[tab.id]}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Content Area */}
        <motion.div
          key={currentTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 transition-all duration-200 hover:shadow-lg hover:border-gray-300"
        >
          {renderSection()}
        </motion.div>
      </div>
    </div>
  );
};

export default SuperAdminPage;
